import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, Shield, TrendingUp, Map, Crown, Globe, Target } from 'lucide-react';
export function GovernmentPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-679">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-680">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-681">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-682">
            <span className="w-8 h-px bg-teal" data-id="element-683"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-684">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-685"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-686">
            Government and Public Sector Applications
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-687">
            SyNeural's platform architecture is designed to help governments and
            public institutions build stronger human capability, more resilient
            communities, better-functioning institutions, and more rigorous
            leadership pipelines.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-688">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-689">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-690">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, connecting
            those gaps to development pathways, and strengthening public systems
            over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-691">
            The platform is relevant across multiple levels of government. At
            the local level, it can help identify community bottlenecks such as
            weak youth pathways, low workforce readiness, or heightened
            misinformation vulnerability. At the regional level, it can support
            education strategy, workforce development, resilience planning, and
            leadership cultivation. At the national level, it can support
            civil-service modernization, ministry-level planning, institutional
            capability mapping, and executive-level readiness.
          </p>
        </motion.div>
      </section>

      {/* Government Levels */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-692">
        <div className="max-w-5xl mx-auto" data-id="element-693">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-694">
            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-695">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center text-teal mx-auto mb-4" data-id="element-696">
                <Building2 size={28} data-id="element-697" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-698">
                Municipal & Local
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-699">
                Community capability patterns and local development
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-700">
              <div className="w-14 h-14 rounded-full bg-navy-light/10 flex items-center justify-center text-navy-light mx-auto mb-4" data-id="element-701">
                <Map size={28} data-id="element-702" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-703">
                Regional & Provincial
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-704">
                Broader planning across education, labor, and resilience
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-705">
              <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center text-navy mx-auto mb-4" data-id="element-706">
                <Globe size={28} data-id="element-707" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-708">
                National Government
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-709">
                Ministry-level planning and civil-service modernization
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-710">
        {/* Municipal and Local Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-711">
          <div className="flex items-center gap-4 mb-6" data-id="element-712">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-713">
              <Building2 size={28} data-id="element-714" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-715">
              Municipal and Local Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-716">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-717">
              At the municipal level, SyNeural can help public leaders and local
              institutions better understand the human capability patterns
              shaping community outcomes. This may include identifying areas
              with weak youth-to-opportunity pipelines, low workforce readiness,
              limited leadership development, or elevated vulnerability to
              rumor, manipulation, and civic instability. This makes the
              platform useful for local education initiatives, youth programs,
              resilience efforts, and community-development planning.
            </p>
          </div>
        </motion.div>

        {/* Regional and Provincial Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-718">
          <div className="flex items-center gap-4 mb-6" data-id="element-719">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-720">
              <Map size={28} data-id="element-721" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-722">
              Regional and Provincial Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-723">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-724">
              At the regional level, SyNeural can support broader planning
              across education, labor, public health, social development, and
              leadership preparation. The platform can help map capability gaps
              across districts or provinces, identify opportunity bottlenecks,
              and support more targeted approaches to workforce development,
              institutional strengthening, and civic resilience. This makes it
              relevant for regional modernization strategies and long-range
              public planning.
            </p>
          </div>
        </motion.div>

        {/* National Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-725">
          <div className="flex items-center gap-4 mb-6" data-id="element-726">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-727">
              <Globe size={28} data-id="element-728" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-729">
              National Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-730">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-731">
              At the national level, SyNeural can function as part of a larger
              capability and readiness architecture. The platform can support
              ministry-level planning, civil-service development, education
              reform, workforce strategy, national resilience analysis, and
              leadership-readiness frameworks. It is designed to help
              governments better understand the human layer beneath
              institutional performance and use that understanding to build
              stronger long-term systems.
            </p>
          </div>
        </motion.div>

        {/* Civil Service Modernization */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-732">
          <div className="flex items-center gap-4 mb-6" data-id="element-733">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-734">
              <Shield size={28} data-id="element-735" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-736">
              Civil Service Modernization
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-737">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-738">
              SyNeural can support civil-service modernization by helping public
              institutions identify the capabilities that matter most in
              authority-bearing roles. These include reasoning quality,
              emotional regulation, consequence awareness, systems thinking,
              integrity under pressure, and responsibility at scale. The
              platform can support development pathways, readiness analysis, and
              more structured approaches to public-sector advancement and
              leadership cultivation.
            </p>
          </div>
        </motion.div>

        {/* Public Workforce and Institutional Readiness */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-739">
          <div className="flex items-center gap-4 mb-6" data-id="element-740">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-741">
              <Users size={28} data-id="element-742" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-743">
              Public Workforce and Institutional Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-744">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-745">
              Governments rely on people in roles where poor judgment can weaken
              trust, create inefficiency, or generate serious public
              consequences. SyNeural helps create a stronger framework for
              understanding readiness across those environments. It can support
              workforce development, public-sector training, role qualification,
              succession planning, and institutional capability-building by
              identifying where readiness is strong, where it is weak, and where
              support systems need to improve.
            </p>
          </div>
        </motion.div>

        {/* Civic Resilience and Community Stability */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-746">
          <div className="flex items-center gap-4 mb-6" data-id="element-747">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-748">
              <Target size={28} data-id="element-749" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-750">
              Civic Resilience and Community Stability
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-751">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-752">
              Public institutions increasingly operate in environments shaped by
              misinformation, distrust, emotional volatility, and fragmented
              civic participation. SyNeural can help governments better
              understand the conditions that strengthen or weaken civic
              resilience, including manipulation vulnerability, low
              consequential thinking, weak opportunity alignment, and
              underdeveloped local leadership. This makes the platform relevant
              not just for internal institutions, but for wider community
              stability and public trust.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development and High-Responsibility Roles */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-753">
          <div className="flex items-center gap-4 mb-6" data-id="element-754">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-755">
              <Crown size={28} data-id="element-756" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-757">
              Leadership Development and High-Responsibility Roles
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-758">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-759">
              As the level of responsibility rises, the cost of poor judgment
              rises with it. SyNeural is designed to become more rigorous as the
              stakes of decision-making increase. In government settings, this
              makes the platform especially relevant for leadership development,
              senior administrative roles, and executive-level readiness. It can
              support more structured approaches to evaluating judgment,
              consequence reasoning, responsibility, and institutional
              reliability in roles where large systems and populations are
              affected.
            </p>
          </div>
        </motion.div>

        {/* Strategic Planning and Capability Mapping */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-760">
          <div className="flex items-center gap-4 mb-6" data-id="element-761">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-762">
              <TrendingUp size={28} data-id="element-763" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-764">
              Strategic Planning and Capability Mapping
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-765">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-766">
              At scale, SyNeural can support governments through aggregate
              capability mapping and strategic analysis. This includes
              identifying regional bottlenecks, workforce-readiness patterns,
              leadership-pipeline depth, misinformation vulnerability, and
              broader opportunity constraints. Over time, this can help support
              more coherent long-range planning for education, labor,
              resilience, public-sector development, and social stability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-767">
        <div className="max-w-4xl mx-auto" data-id="element-768">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-769">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-770">
              Why It Matters for Government
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-771">
              Governments are often asked to solve problems at the policy level
              that are deeply connected to human capability, institutional
              readiness, and leadership quality. SyNeural offers a more
              structured way to examine those underlying conditions. It helps
              connect human development to public performance, civic resilience
              to institutional trust, and leadership readiness to long-term
              governance stability. That makes it relevant not only as a
              platform for analysis, but as a broader framework for public
              capability and institutional strength.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}